/* ============================================================================
   complete-the-look.js — <sf-bundle> (Sneak Freak · B8)
   ----------------------------------------------------------------------------
   Enhances sections/complete-the-look.liquid. Needs window.theme (theme.js).

   Behaviour (mirrors the prototype's bundle.js):
     · choosing a size ticks the row; a ticked row with no size is `.needs-size`
     · footer text: nothing selected / choose a size / "N pieces · $total"
       (total via theme.money — the CONTRACT §0.3 exception, documented in the B8 report;
        prices come from data-price on the row / selected <option>, in cents)
     · Add → theme.cart.add(items[]) with .is-loading / aria-busy, error row, toast,
       theme.track('bundle_add') when available, clears the selection afterwards
     · data-mode="shell" (source = complementary): fetches data-url (this same section
       rendered by the Product Recommendations API) and swaps [data-bundle-content]

   Attributes: data-section-id data-mode data-url data-design-mode data-str-* (strings)
   Rows:       [data-bundle-row][data-price][data-handle] > [data-bundle-check]
               [data-bundle-size] (select, option[data-price]) | [data-bundle-variant] (hidden, one size)
   Footer:     [data-bundle-total] [data-bundle-add] [data-bundle-error]
   ============================================================================ */
(function () {
  'use strict';
  if (customElements.get('sf-bundle')) return;

  function fill(template, vars) {
    return String(template || '').replace(/\[(\w+)\]/g, (m, k) => (k in vars ? vars[k] : m));
  }

  class SFBundle extends HTMLElement {
    connectedCallback() {
      this._ctrl = new AbortController();
      const signal = this._ctrl.signal;
      this._busy = false;

      this.addEventListener('change', (e) => this._onChange(e), { signal });
      this.addEventListener('submit', (e) => this._onSubmit(e), { signal });

      if (this.getAttribute('data-mode') === 'shell' && this.getAttribute('data-url')) {
        this._fetch(this.getAttribute('data-url'));
      } else {
        this.sync();
      }
    }

    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
    }

    /* ---- DOM helpers ---- */
    get rows() {
      return Array.from(this.querySelectorAll('[data-bundle-row]'));
    }
    get totalEl() {
      return this.querySelector('[data-bundle-total]');
    }
    get addBtn() {
      return this.querySelector('[data-bundle-add]');
    }
    get errorEl() {
      return this.querySelector('[data-bundle-error]');
    }
    str(name) {
      return this.getAttribute('data-str-' + name) || '';
    }

    /** @returns {{id:number, price:number, row:Element}|null} the row's chosen variant, or null */
    _selection(row) {
      const check = row.querySelector('[data-bundle-check]');
      if (!check || !check.checked || check.disabled) return null;
      const select = row.querySelector('[data-bundle-size]');
      if (select) {
        const opt = select.options[select.selectedIndex];
        if (!select.value || !opt || opt.disabled) return { id: 0, price: 0, row };
        return { id: Number(select.value), price: Number(opt.getAttribute('data-price') || row.getAttribute('data-price') || 0), row };
      }
      const hidden = row.querySelector('[data-bundle-variant]');
      if (hidden && hidden.value) {
        return { id: Number(hidden.value), price: Number(hidden.getAttribute('data-price') || row.getAttribute('data-price') || 0), row };
      }
      return { id: 0, price: 0, row };
    }

    /* ---- state ---- */
    sync() {
      const theme = window.theme || {};
      let chosen = 0;
      let needSize = 0;
      let total = 0;

      this.rows.forEach((row) => {
        const sel = this._selection(row);
        row.classList.toggle('is-on', !!sel);
        const needs = !!sel && !sel.id;
        row.classList.toggle('needs-size', needs);
        row.classList.toggle('has-size', !!(sel && sel.id));
        const select = row.querySelector('[data-bundle-size]');
        if (select) select.setAttribute('aria-invalid', needs ? 'true' : 'false');
        if (!sel) return;
        if (sel.id) {
          chosen += 1;
          total += sel.price;
        } else {
          needSize += 1;
        }
      });

      const totalEl = this.totalEl;
      if (totalEl) {
        let text;
        if (chosen === 0) {
          text = needSize ? this.str('needs-size') : this.str('nothing');
        } else {
          const money = theme.money ? theme.money(total) : String(total / 100);
          text = fill(chosen === 1 ? this.str('total-one') : this.str('total-other'), { n: chosen, total: money });
        }
        totalEl.textContent = text;
        totalEl.classList.toggle('is-warn', chosen === 0 && needSize > 0);
      }

      const btn = this.addBtn;
      if (btn) {
        btn.disabled = chosen === 0 || this._busy;
        const label = btn.querySelector('.btn__label') || btn;
        label.textContent = chosen > 1 ? fill(this.str('add-count'), { n: chosen }) : this.str('add');
      }
      return { chosen, needSize, total };
    }

    _onChange(e) {
      const target = e.target;
      if (!target) return;
      const select = target.closest && target.closest('[data-bundle-size]');
      if (select && select.value) {
        const row = select.closest('[data-bundle-row]');
        const check = row && row.querySelector('[data-bundle-check]');
        if (check && !check.checked && !check.disabled) check.checked = true;
      }
      this._showError('');
      this.sync();
    }

    /* ---- add ---- */
    async _onSubmit(e) {
      e.preventDefault();
      if (this._busy) return;
      const theme = window.theme || {};
      const items = this.rows
        .map((row) => this._selection(row))
        .filter((sel) => sel && sel.id)
        .map((sel) => ({ id: sel.id, quantity: 1 }));
      if (!items.length) {
        this.sync();
        return;
      }
      if (!theme.cart || !theme.cart.add) {
        // No runtime: let the browser post the form (Shopify accepts items[i][id]).
        e.target.submit();
        return;
      }

      const btn = this.addBtn;
      this._busy = true;
      this._showError('');
      if (btn) {
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
      }
      const total = this.rows.reduce((sum, row) => {
        const sel = this._selection(row);
        return sel && sel.id ? sum + sel.price : sum;
      }, 0);

      try {
        await theme.cart.add(items);
        if (theme.track) theme.track('bundle_add', { count: items.length, total });
        if (theme.toast) {
          theme.toast(items.length === 1 ? this.str('added-one') : fill(this.str('added-other'), { n: items.length }), { type: 'success' });
        }
        this.rows.forEach((row) => {
          const check = row.querySelector('[data-bundle-check]');
          if (check) check.checked = false;
          const select = row.querySelector('[data-bundle-size]');
          if (select) select.value = '';
        });
      } catch (err) {
        const message = (err && (err.description || err.message)) || this.str('error');
        this._showError(message);
      } finally {
        this._busy = false;
        if (btn) {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
        }
        this.sync();
      }
    }

    _showError(message) {
      const el = this.errorEl;
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    /* ---- complementary: fetch this section through the Recommendations API ---- */
    async _fetch(url) {
      const theme = window.theme || {};
      const host = this.querySelector('[data-bundle-content]');
      if (!host) return;
      host.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(url, { signal: this._ctrl.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error('recommendations ' + res.status);
        const html = await res.text();
        const id = this.getAttribute('data-section-id');
        const wrapper = theme.sectionHtml ? theme.sectionHtml(html, id) : new DOMParser().parseFromString(html, 'text/html').body;
        const content = wrapper && wrapper.querySelector('[data-bundle-content]');
        const rows = content ? content.querySelectorAll('[data-bundle-row]') : [];
        if (!content || !rows.length) {
          this._empty();
          return;
        }
        host.replaceChildren(...Array.from(content.childNodes));
        this.setAttribute('data-mode', 'fetched');
        this.sync();
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        this._empty();
      } finally {
        host.removeAttribute('aria-busy');
      }
    }

    _empty() {
      if (this.hasAttribute('data-design-mode')) {
        const host = this.querySelector('[data-bundle-content]');
        if (host) host.replaceChildren();
        this.classList.add('is-empty');
        return;
      }
      this.hidden = true;
    }
  }

  customElements.define('sf-bundle', SFBundle);
})();
